import React, {useCallback, useEffect, useMemo, useState} from "react";
import {AccountInfo, EventMessage, EventType} from "@azure/msal-browser";
import {useMsal} from "@azure/msal-react";
import {API_INTERACTION_REQUIRED_EVENT, acquireTokenSilent, loginRedirect, logoutRedirect, msalInstance} from "../services/authService";

interface AuthContextType {
  account: AccountInfo | null;
  login: () => void;
  logout: () => void;
  authenticationRequired: boolean;
  getToken: () => Promise<string>;
}

export const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
  const { accounts } = useMsal();
  const [account, setAccount] = useState<AccountInfo | null>(accounts[0] || null);
  const [authenticationRequired, setAuthenticationRequired] = useState(false);

  useEffect(() => {
    const current = msalInstance.getActiveAccount() || accounts[0] || null;
    if (current) {
      msalInstance.setActiveAccount(current);
    }
    setAccount(current);
  }, [accounts]);

  // Listen for MSAL account changes (login/logout)
  useEffect(() => {
    const callbackId = msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const authResult = event.payload as any;
        msalInstance.setActiveAccount(authResult.account);
        setAccount(authResult.account);
        setAuthenticationRequired(false);
      }
      if (event.eventType === EventType.HANDLE_REDIRECT_END) {
        const current = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null;
        setAccount(current);
      }
      if (event.eventType === EventType.LOGOUT_SUCCESS) {
        setAccount(null);
        setAuthenticationRequired(false);
      }
    });
    return () => {
      if (callbackId) msalInstance.removeEventCallback(callbackId);
    };
  }, []);

  useEffect(() => {
    const onInteractionRequired = () => setAuthenticationRequired(true);
    window.addEventListener(API_INTERACTION_REQUIRED_EVENT, onInteractionRequired);
    return () => window.removeEventListener(API_INTERACTION_REQUIRED_EVENT, onInteractionRequired);
  }, []);

  const login = useCallback(() => {
    setAuthenticationRequired(false);
    loginRedirect();
  }, []);
  const logout = useCallback(() => logoutRedirect(), []);
  const getToken = useCallback(() => acquireTokenSilent(), []);

  const value = useMemo<AuthContextType>(
      () => ({account, login, logout, authenticationRequired, getToken}),
      [account, login, logout, authenticationRequired, getToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
